/* Timeline scrubber — markers make the exercise's narrative visible.
 * Checkpoints, injects, engagements, first detections: the story of the run (UI-UX-DESIGN.md §7).
 */
import { Flag, GitBranch, Rewind, Save, Swords, Radar } from 'lucide-react'
import { useDeck } from '../state/store'
import { simClock } from '../lib/format'

type MarkerKind = 'checkpoint' | 'inject' | 'engagement' | 'detection'

interface Marker {
  at: number
  kind: MarkerKind
  label: string
}

const MARKERS: Marker[] = [
  { at: 1200, kind: 'checkpoint', label: 'Autosave' },
  { at: 2640, kind: 'inject', label: 'EXCON inject — border violation report' },
  { at: 4800, kind: 'checkpoint', label: 'Autosave' },
  { at: 6300, kind: 'detection', label: 'First hostile air detection' },
  { at: 7144, kind: 'engagement', label: 'Mk 91 director destroyed' },
  { at: 8402, kind: 'detection', label: 'TRACK 4471 acquired' },
]

const MARKER_STYLE: Record<MarkerKind, { token: string; icon: typeof Flag }> = {
  checkpoint: { token: 'var(--color-text-3)', icon: Save },
  inject: { token: 'var(--color-accent-hi)', icon: Flag },
  engagement: { token: 'var(--color-crit)', icon: Swords },
  detection: { token: 'var(--color-unknown)', icon: Radar },
}

export function TimeScrubber() {
  const epochIso = useDeck((s) => s.epochIso)
  // 12 hours, the scenario's own duration. A constant here would mean the scrubber lied about the
  // exercise length for any scenario but the demo.
  const durationSec = useDeck((s) => s.durationSec)
  const elapsedSec = useDeck((s) => s.elapsedSec)
  const total = durationSec
  const pct = Math.min(100, (elapsedSec / total) * 100)

  const startClock = simClock(epochIso, 0)
  const endClock = simClock(epochIso, total)
  const nowClock = simClock(epochIso, elapsedSec)

  return (
    <div
      className="flex h-10 shrink-0 items-center gap-3 border-b px-3"
      style={{ background: 'var(--color-surface-1)', borderColor: 'var(--color-line-2)' }}
    >
      <span className="num shrink-0" style={{ fontSize: 10, color: 'var(--color-text-3)' }}>
        {startClock.time.slice(0, 5)}
      </span>

      {/* Track */}
      <div className="relative min-w-0 flex-1" style={{ height: 24 }}>
        {/* Rail */}
        <div
          className="absolute top-1/2 h-1 w-full -translate-y-1/2 rounded-full"
          style={{ background: 'var(--color-surface-3)' }}
        />
        {/* Elapsed */}
        <div
          className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full"
          style={{ width: `${pct}%`, background: 'var(--color-accent)', opacity: 0.6 }}
        />

        {/* Markers */}
        {MARKERS.map((m) => {
          const { token, icon: Icon } = MARKER_STYLE[m.kind]
          const left = (m.at / total) * 100
          return (
            <button
              key={`${m.kind}-${m.at}`}
              className="group absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${left}%` }}
              aria-label={`${m.label} at ${simClock(epochIso, m.at).time}`}
              title={`${simClock(epochIso, m.at).time} — ${m.label}`}
            >
              <span
                className="grid size-4 place-items-center rounded-sm transition-transform group-hover:scale-125"
                style={{ background: 'var(--color-surface-0)', border: `1px solid ${token}` }}
              >
                <Icon size={9} style={{ color: token }} aria-hidden />
              </span>
            </button>
          )
        })}

        {/* Playhead */}
        <div
          className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${pct}%` }}
        >
          <span
            className="block h-4 w-0.5 rounded-full"
            style={{ background: 'var(--color-accent-hi)', boxShadow: '0 0 6px var(--color-accent)' }}
          />
        </div>
      </div>

      <span className="num shrink-0" style={{ fontSize: 10, color: 'var(--color-text-3)' }}>
        {endClock.time.slice(0, 5)}
      </span>

      <div className="h-4 w-px shrink-0" style={{ background: 'var(--color-line-2)' }} />

      <span className="num shrink-0" style={{ fontSize: 11, color: 'var(--color-text-1)' }}>
        {nowClock.time}
      </span>

      <button className="ctl shrink-0" aria-label="Rewind to previous checkpoint" title="Rewind to previous checkpoint">
        <Rewind size={13} />
      </button>
      <button className="ctl shrink-0" aria-label="Branch from here" title="Branch from here — creates a new run lineage">
        <GitBranch size={13} />
      </button>
    </div>
  )
}
